import { CalendarDate, CalendarDateTime } from '@internationalized/date';
import {
    createProperties,
    createRoot,
    type Field,
    Form,
    type FormState,
    type TypeToFields,
} from '@jmvdevelop/svelte-form';
import type { ShareableContainer } from '$lib/container/shareableContainer';
import { notNullableOrThrow } from '$lib/utils/assert';
import { mergeObject } from '$lib/utils/mergeObject';
import { fail, ok, type Result } from '$lib/utils/result.js';
import { temporizePromise } from '$lib/utils/temporizePromise.js';
import { ImmURL } from '$lib/utils/url/ImmURL';
import { getUtmVariablesFromUrl } from '$lib/utils/utmVariables';
import type { JsonValue } from '$lib/utils/zod';
import { validateEmail } from '$lib/validator/email';
import { notBlankAndNotNull, notNull } from '$lib/validator/validator.js';
import {
    MultiQuote_CreateMutationDocument,
    type MultiQuote_CreateMutationMutation,
} from '$gql/graphql.js';
import { getRequestInfoInput } from '$components/Ad/utils';
import * as Assurance from '$components/MultiQuoteForm/Assurance/AssuranceFormData';
import * as CarRental from '$components/MultiQuoteForm/CarRental/CarRentalFormData';
import * as Construction from '$components/MultiQuoteForm/Construction/ConstructionFormData';
import * as Consultant from '$components/MultiQuoteForm/Consultant/ConsultantFormData';
import * as OfficeFurniture from '$components/MultiQuoteForm/OfficeFurniture/OfficeFurnitureFormData';
import * as Solar from '$components/MultiQuoteForm/Solar/SolarFormData';
import * as Transit from '$components/MultiQuoteForm/Transit/TransitFormData';

export type MultiQuoteFormData = {
    user: number | null;
    message: string;
    categoryId: number | null;
    relatedCmsPostId: number | null;
    country: string | null;
    city: number | null;
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    phone: string | null;
    carRental: CarRental.CarRentalFormData;
    assurance: Assurance.AssuranceFormData;
    transit: Transit.TransitFormData;
    solar: Solar.SolarFormData;
    consultant: Consultant.ConsultantFormData;
    officeFurniture: OfficeFurniture.OfficeFurnitureFormData;
    construction: Construction.ConstructionFormData;
};

type FormData = MultiQuoteFormData;

export type Fields<TFormValues> = TypeToFields<
    TFormValues,
    FormData,
    keyof FormData,
    {
        carRental: CarRental.Fields<TFormValues>;
        assurance: Assurance.Fields<TFormValues>;
        transit: Transit.Fields<TFormValues>;
        solar: Solar.Fields<TFormValues>;
        consultant: Consultant.Fields<TFormValues>;
        officeFurniture: OfficeFurniture.Fields<TFormValues>;
        construction: Construction.Fields<TFormValues>;
    }
>;

export const empty: FormData = {
    user: null,
    message: '',
    categoryId: null,
    country: null,
    city: null,
    phone: null,
    firstname: null,
    lastname: null,
    email: null,
    relatedCmsPostId: null,
    carRental: CarRental.empty,
    assurance: Assurance.empty,
    transit: Transit.empty,
    solar: Solar.empty,
    consultant: Consultant.empty,
    officeFurniture: OfficeFurniture.empty,
    construction: Construction.empty,
};

export function createFields<TValues>(self: Field<TValues, FormData>): Fields<TValues> {
    const properties = createProperties(self, [
        'user',
        'message',
        'categoryId',
        'country',
        'city',
        'phone',
        'firstname',
        'lastname',
        'email',
        'relatedCmsPostId',
        'carRental',
        'assurance',
        'transit',
        'solar',
        'consultant',
        'officeFurniture',
        'construction',
    ] as const);

    return {
        self: self,
        ...properties,
        carRental: CarRental.createFields(properties.carRental),
        assurance: Assurance.createFields(properties.assurance),
        transit: Transit.createFields(properties.transit),
        solar: Solar.createFields(properties.solar),
        consultant: Consultant.createFields(properties.consultant),
        officeFurniture: OfficeFurniture.createFields(properties.officeFurniture),
        construction: Construction.createFields(properties.construction),
    };
}

export function validate(
    form: Form<FormData, string>,
    fields: Fields<FormData>,
    state: FormState<FormData, string>,
) {
    const values = state.values;

    if (values.user == null) {
        state = notBlankAndNotNull(values.firstname, {
            form,
            field: fields.firstname,
            message: 'Vous devez saisir votre prénom',
        })(state);

        state = notBlankAndNotNull(values.lastname, {
            form,
            field: fields.lastname,
            message: 'Vous devez saisir votre nom',
        })(state);

        state = validateEmail(values.email, {
            form,
            field: fields.email,
            notBlank: true,
            message: 'Vous devez saisir une adresse email valide',
        })(state);
    }

    state = notBlankAndNotNull(values.message, {
        form,
        field: fields.message,
        message: 'Vous devez saisir du texte comme message',
    })(state);

    state = notBlankAndNotNull(values.country, {
        form,
        field: fields.country,
        message: 'Vous devez choisir un pays',
    })(state);

    state = notNull(values.categoryId, {
        form,
        field: fields.categoryId,
        message: 'Vous devez specifier une catégorie',
    })(state);

    state = CarRental.validateCarRental(form, values.carRental, fields.carRental)(state);
    state = Assurance.validateAssurance(form, values.assurance, fields.assurance)(state);
    state = Transit.validateTransit(form, values.transit, fields.transit)(state);
    state = Solar.validateSolar(form, values.solar, fields.solar)(state);

    state = Consultant.validateConsultant(
        form,
        values.consultant,
        fields.consultant,
    )(state);
    state = OfficeFurniture.validateOfficeFurniture(
        form,
        values.officeFurniture,
        fields.officeFurniture,
    )(state);
    state = Construction.validateConstruction(
        form,
        values.construction,
        fields.construction,
    )(state);

    return state;
}

export function createForm(
    id: string,
    initialValues: Partial<FormData> = empty,
): [Form<FormData, string>, Fields<FormData>] {
    const fields = createFields<FormData>(createRoot<FormData>());
    const form = new Form<FormData, string>(
        { initialValues: mergeObject(empty, initialValues) },
        id,
    );

    form.addValidator((state) => {
        return validate(form, fields, state);
    });

    return [form, fields];
}

type OnSubmit = (
    form: Form<FormData, string>,
) => Promise<Result<MultiQuote_CreateMutationMutation['multiquote']['create'], string>>;

type ExtraValueScalar =
    | string
    | number
    | null
    | undefined
    | CalendarDateTime
    | CalendarDate;
type ExtraValue = ExtraValueScalar | readonly ExtraValueScalar[];

function isExtraValueArray(arg: ExtraValue): arg is readonly ExtraValueScalar[] {
    return Array.isArray(arg);
}

function formatExtraValue(
    deps: Pick<ShareableContainer, 'intl'>,
    value: ExtraValue,
): string {
    const { intl } = deps;

    if (value instanceof CalendarDateTime) {
        return intl().formatCalendarDateTime(value, 'PPPp');
    } else if (value instanceof CalendarDate) {
        return intl().formatCalendarDate(value, 'PPP');
    } else if (isExtraValueArray(value)) {
        return value.map((v) => formatExtraValue(deps, v)).join(', ');
    } else if (typeof value === 'number') {
        return intl().formatInt(value);
    } else if (typeof value === 'string') {
        return value;
    } else {
        return '';
    }
}

function formatExtraFields(
    deps: Pick<ShareableContainer, 'intl'>,
    fields: readonly Readonly<[string, ExtraValue]>[],
): { label: string; value: string }[] {
    const lines: { label: string; value: string }[] = [];
    for (const [label, value] of fields) {
        if (value != null) {
            lines.push({ label: label, value: formatExtraValue(deps, value) });
        }
    }

    return lines;
}

function extractExtraFields(
    deps: Pick<ShareableContainer, 'intl'>,
    values: FormData,
): JsonValue | null {
    if (values.categoryId === 411) {
        const carRental = values.carRental;
        return formatExtraFields(deps, [
            ['Type de véhicule', carRental.vehicleType],
            ['Capacité du véhicule', carRental.vehicleCapacity],
            ['Chauffeur ?', carRental.withDriver],
            ['Lieu de prise en charge', carRental.pickupLocation],
            ['Date et heure de départ', carRental.departureDate],
            ['Date et heure de retour', carRental.returnDate],
        ]);
    } else if (values.categoryId === 173 || values.categoryId === 174) {
        const assurance = values.assurance;
        return formatExtraFields(deps, [
            ['Type d’assurance recherchée', assurance.assuranceType],
            [
                'Êtes-vous un particulier ou un professionnel',
                assurance.isIndividualOrProfessional,
            ],
            ...(assurance.assuranceType === 'other'
                ? ([
                      [`Précisez le type d'assurance`, assurance.assuranceTypeOther],
                  ] as const)
                : []),
            ...(assurance.assuranceType === 'auto'
                ? ([
                      ['Type de véhicule', assurance.vehicleType],
                      ['Marque et modèle', assurance.vehicleBrandAndModel],
                      ['Année de mise en circulation', assurance.vehicleYear],
                      ['Usage du véhicule', assurance.vehicleUsage],
                      ['Garantie souhaitée', assurance.desiredCoverage],
                  ] as const)
                : []),
            ...(assurance.assuranceType === 'home'
                ? ([
                      ['Type de logement', assurance.housingType],
                      ['Propriétaire ou Locataire ?', assurance.isOwnerOrTenant],
                      ['Surface du logement', assurance.housingSurface],
                      ['Valeur du bien à assurer', assurance.insuredGoodsValue],
                  ] as const)
                : []),
            ...(assurance.assuranceType === 'health'
                ? ([
                      ['Âge du souscripteur', assurance.subscriberAge],
                      ['Nombre de personnes à couvrir', assurance.numberOfPeopleCovered],
                      [
                          'Niveau de remboursement souhaité',
                          assurance.desiredReimbursementLevel,
                      ],
                  ] as const)
                : []),
            ...(assurance.assuranceType === 'pro'
                ? ([
                      ['Secteur d’activité', assurance.activitySector],
                      ['Nombre d’employés', assurance.employeeCount],
                      ['Type de couverture souhaitée', assurance.coverageType],
                  ] as const)
                : []),
            ...(assurance.assuranceType === 'travel'
                ? ([
                      ['Destination du voyage', assurance.travelDestination],
                      ['Durée du séjour', assurance.travelDuration],
                      ['Nombre de personnes à couvrir', assurance.travelNumberOfPeople],
                  ] as const)
                : []),
            ...(assurance.assuranceType === 'pet'
                ? ([
                      ['Type d’animal', assurance.animalType],
                      ['Race de l’animal', assurance.animalBreed],
                      ['Âge de l’animal', assurance.animalAge],
                  ] as const)
                : []),
            ...(assurance.assuranceType === 'life'
                ? ([
                      [
                          'Quel est votre objectif principal ?',
                          assurance.lifeMainObjective,
                      ],
                      ['Âge du souscripteur', assurance.lifeSubscriberAge],
                      ['Montant estimé à assurer', assurance.lifeEstimatedAmount],
                      [
                          'Bénéficiaire(s) désigné(s) ?',
                          assurance.lifeDesignatedBeneficiary,
                      ],
                  ] as const)
                : []),
            ...(assurance.assuranceType === 'borrower'
                ? ([
                      ['Montant du prêt', assurance.borrowerLoanAmount],
                      ['Durée du prêt', assurance.borrowerLoanDuration],
                      ['Type de bien financé', assurance.borrowerPropertyTypeFinanced],
                      [
                          'Avez-vous des problèmes de santé connus ?',
                          assurance.borrowerHasKnownHealthIssues,
                      ],
                      [
                          'Souhaitez-vous déléguer votre assurance (hors banque) ?',
                          assurance.borrowerWantsToDelegateInsurance,
                      ],
                  ] as const)
                : []),
            ...(assurance.assuranceType === 'temporary'
                ? ([
                      [
                          'Type de bien ou situation à assurer temporairement',
                          assurance.temporaryInsuredItemOrSituation,
                      ],
                      [
                          'Durée souhaitée de l’assurance',
                          assurance.temporaryDesiredDuration,
                      ],
                      [
                          'Motif de la souscription temporaire',
                          assurance.temporarySubscriptionReason,
                      ],
                      [
                          'Date de début de la couverture',
                          assurance.temporaryCoverageStartDate,
                      ],
                  ] as const)
                : []),
        ]);
    } else if (values.categoryId === 323) {
        const transit = values.transit;
        return formatExtraFields(deps, [
            [`Quel type de transit recherchez-vous ?`, transit.transitType],
            [`Votre marchandise est-elle déjà disponible ?`, transit.isGoodsAvailable],
            [
                `Quel est le point de départ de votre marchandise ?`,
                transit.goodsStartPoint,
            ],
            [`Quelle est la destination finale ?`, transit.goodsFinalDestination],
            [
                `Avez-vous besoin d’un service de dédouanement ?`,
                transit.clearanceServiceRequired,
            ],
            [
                `Souhaitez-vous un service d’entreposage temporaire ?`,
                transit.temporaryStorageRequired,
            ],
            [`Quel est le type de marchandise ?`, transit.goodsType],
            [`Quel est le volume estimé de la marchandise ?`, transit.goodsVolume],
            [`Quel est le poids estimé de la marchandise ?`, transit.goodsWeight],
            [`Avez-vous des exigences particulières ?`, transit.specificRequirements],
            [`Quand souhaitez-vous organiser le transit ?`, transit.transitDate],
        ]);
    } else if (values.categoryId === 167) {
        const solar = values.solar;
        return formatExtraFields(deps, [
            [`Type de demande`, solar.requestType],
            [
                `Consommation moyenne ou besoins estimés`,
                solar.averageConsumptionOrEstimatedNeeds,
            ],
            [`Quel est votre objectif principal ?`, solar.mainObjective],
            [`Quand souhaitez-vous démarrer le projet ?`, solar.projectStartDate],
        ]);
    } else if (values.categoryId === 143) {
        const consultant = values.consultant;
        return formatExtraFields(deps, [
            [`Type de besoin`, consultant.needType],
            [`Vous êtes un / une`, consultant.orgType],
        ]);
    } else if (values.categoryId === 108) {
        const officeFurniture = values.officeFurniture;
        return formatExtraFields(deps, [
            [`Vous êtes`, officeFurniture.entityType],
            [`Quels types de mobilier recherchez-vous ?`, officeFurniture.furnitureTypes],
            [`Volume estimé ou nombre de postes à équiper`, officeFurniture.volume],
        ]);
    } else if (values.categoryId === 430) {
        const construction = values.construction;
        return formatExtraFields(deps, [
            [`Quel type de projet souhaitez-vous réaliser ?`, construction.projectType],
            [`Pour quel type de bâtiment ?`, construction.buildingType],
        ]);
    }

    return null;
}

export const createOnSubmit: (
    deps: Pick<ShareableContainer, 'intl' | 'apollo'>,
) => OnSubmit = (deps) => (form) => {
    return temporizePromise(
        1000,
        (async () => {
            try {
                const { apollo } = deps;
                const values = form.getState().values;
                const result = await apollo().mutate({
                    mutation: MultiQuote_CreateMutationDocument,
                    variables: {
                        input: {
                            message: values.message,
                            cityId: values.city,
                            phone: values.phone,
                            firstname: values.firstname,
                            lastname: values.lastname,
                            email: values.email,
                            relatedCmsPostId: values.relatedCmsPostId,
                            categoryId: notNullableOrThrow(values.categoryId),
                            country: notNullableOrThrow(values.country),
                            requestInfo: getRequestInfoInput(
                                new URL(window.document.location.href),
                            ),
                            utmVariables: getUtmVariablesFromUrl(
                                new ImmURL(window.document.location.href),
                            ),
                            extraFields: extractExtraFields(deps, values),
                        },
                    },
                });
                if (result.errors != null || result.data == null) {
                    return fail(result.errors?.toString() ?? 'Unknown error');
                }

                return ok(result.data.multiquote.create);
            } catch (e) {
                console.error(e);
                return fail('Erreur inconnue.');
            }
        })(),
    );
};
